<template>
  <div class="create-batch-view">
    <div class="p-10 ml-13">
      <div class="create-batch-header mb-5">
        <div class="create-batch-header-left">
          <button
            type="button"
            class="page-back-button"
            @click="handleBack"
          >
            <md-icon md-src="/static/images/icon-arrow-left.svg" />
          </button>

          <label-data-title
            label="Detalhes do lote"
            :data="label"
          />
        </div>

        <div
          v-if="isViewing"
          class="create-batch-print-buttons"
        >
          <button
            class="btn btn-primary"
            @click="validatePrintingSystemForBatch"
          >
            <svg-loader
              class="icon-print"
              src="/static/images/icon-print-alternative-white.svg"
            />

            {{ printBatchLabelText }}
          </button>

          <button
            class="btn btn-primary"
            @click="validatePrintingSystemForAllSamples"
          >
            <svg-loader
              class="icon-print"
              src="/static/images/icon-print-alternative-white.svg"
            />

            {{ printLabelsText }}
          </button>

          <printer-connection-status
            label-text="Status:"
            @download-plugin-requested="handleDownloadPluginRequested"
          />

          <printer-config-button
            v-if="toggle.printerPlugin && webSocketStatus.connectionState === 'connected'"
            tooltip-direction="left"
            tooltip-text="Configurações da Impressora"
          />

          <printer-calibration />

          <printer-help-button />
        </div>
      </div>

      <card
        v-if="!batch.samplesCollected"
        title="Incluir amostra no lote"
        class="create-batch-card"
      >
        <div class="create-batch-form">
          <md-input-container class="create-batch-input-sample-code">
            <!-- @note This field is not using a v-model directive intentionally because of the barcode scanner behavior. -->
            <md-input
              ref="sampleBarcodeInputRef"
              name="sampleBarcode"
              type="text"
              placeholder="Adicionar amostra com leitor ou manualmente"
              autocomplete="off"
              autofocus
              :disabled="isEditingDisabled"
              @keyup.enter.native.prevent="handleAddSample"
              @input.native="handleInputBarcode"
            />

            <md-icon md-src="/static/images/icon-barcode.svg" />
          </md-input-container>

          <md-button
            class="btn btn-primary"
            :disabled="isEditingDisabled"
            @click="handleAddSample"
          >
            Adicionar
          </md-button>
        </div>
      </card>

      <div class="create-batch-details">
        <batch-card-skeleton
          v-if="isBatchFetching"
          class="create-batch-details-preview"
        />

        <batch-card
          v-else
          class="create-batch-details-preview"
          :index="0"
          :batch="batch"
          is-details-mode
        />

        <card class="create-batch-details-samples">
          <batch-sample-list
            v-if="batch"
            :key="$route.params.batchid"
            ref="batchSampleListRef"
            :is-editing-disabled="isEditingDisabled"
            :is-loading="isBatchListLoading"
            :batch="batch"
            @request-fetch-batch="fetchBatch"
          />
        </card>
      </div>
    </div>

    <modal
      ref="moveSampleToCurrentBatchModalRef"
      ref-name="moveSampleToCurrentBatchModalRef"
      template="neutral"
      :title="null"
      :icon-enabled="false"
    >
      <aside class="move-sample-current-batch-modal">
        <header class="move-sample-current-batch-modal-header">
          <h2 class="move-sample-current-batch-modal-title">
            Amostra já inserida no lote
            <router-link
              :to="`/print-tag/batches/${sampleToMove?.batch_id ?? sampleToMove?.batch_identifier}`"
              target="_blank"
              class="move-sample-current-batch-modal-link"
            >
              {{ sampleToMove?.batch_identifier }}
            </router-link>
            . Deseja inserir no lote atual?
          </h2>
        </header>

        <div class="move-sample-current-batch-modal-body">
          Ao continuar, a amostra será removida do lote
          {{ sampleToMove?.batch_identifier }} e inserida no lote atual.
        </div>

        <footer class="move-sample-current-batch-modal-footer">
          <md-button
            class="btn btn-nonenary"
            :disabled="isSampleBeingMoved"
            @click="handleCancelMoveSampleToCurrentBatch"
          >
            Cancelar
          </md-button>

          <md-button
            class="btn btn-primary"
            :disabled="isSampleBeingMoved"
            @click="handleMoveSampleToCurrentBatch"
          >
            Continuar
          </md-button>
        </footer>
      </aside>
    </modal>

    <modal
      ref="printingModal"
      template="loading"
      title="Aguarde"
      ref-name="printingModal"
    >
      <p>Imprimindo etiquetas...</p>
    </modal>

    <modal
      ref="moveSampleToBatchLoadingModal"
      template="loading"
      ref-name="moveSampleToBatchLoadingModal"
      title="Aguarde"
    >
      <p>Movendo amostra...</p>
    </modal>

    <print-server-alert ref="printServerAlertModal" />

    <printer-plugin-download-modal
      ref="printerPluginDownloadModal"
      ref-name="printerPluginDownloadModal"
    />

    <printer-modal ref="printerModal" />

    <printer-plugin-download-modal
      ref="webSocketPluginDownloadModal"
      ref-name="webSocketPluginDownloadModal"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { eventBus, OPEN_PRINTER_PLUGIN_MODAL } from '@/api/eventbus'
import notify from '@/api/notification'
import batchService from '@/api/batch'
import sampleService from '@/api/sample'

import Card from '@/components/common/Card'
import LabelDataTitle from '@/components/common/LabelDataTitle'
import BatchCard from '@/components/PrintLabels/Batches/Cards/BatchCard'
import BatchCardSkeleton from '@/components/PrintLabels/Batches/Cards/BatchCardSkeleton'
import PrintServerAlert from '@/components/Dashboard/PrintServerAlert'
import PrinterPluginDownloadModal from '@/components/common/printer/PrinterPluginDownloadModal'
import PrinterConnectionStatus from '@/components/common/printer/PrinterConnectionStatus'
import PrinterCalibration from '@/components/common/calibration/PrinterCalibration'
import PrinterHelpButton from '@/components/common/printer/PrinterHelpButton'
import PrinterModal from '@/components/common/printer/PrinterModal'
import PrinterConfigButton from '@/components/common/printer/PrinterConfigButton'

import PrintWebSocketService from '@/services/PrintWebSocketService'

import BatchSampleList from './components/BatchSampleList'
import { refreshBatchDetailsAfterSamplePrint } from './batchPrintRefresh'

const PLACEHOLDER_BATCH = {
  total_samples: 0,
  created_at: new Date(),
  state: 'OPENNED',
}

export default {
  name: 'Batch',
  components: {
    Card,
    LabelDataTitle,
    BatchCard,
    BatchCardSkeleton,
    BatchSampleList,
    PrintServerAlert,
    PrinterPluginDownloadModal,
    PrinterConnectionStatus,
    PrinterCalibration,
    PrinterHelpButton,
    PrinterModal,
    PrinterConfigButton,
  },
  data() {
    return {
      sampleBarcode: '',
      isBatchFetching: false,
      isAddingSample: false,
      batch: PLACEHOLDER_BATCH,
      sampleToMove: undefined,
      isSampleBeingMoved: false,
      lastInputType: '',
      lastInputTimestamp: 0,
    }
  },
  computed: {
    ...mapGetters([
      'webSocketStatus',
      'toggle',
      'profile',
      'defaultPrinter',
      'batchSamplesSorted',
      'bathSelectedSamples',
    ]),
    isBatchListLoading() {
      return this.isBatchFetching || this.isAddingSample
    },
    printBatchLabelText() {
      if (this.batch.source === 'AOLWS') {
        if (this.batch.state === 'CLOSED') {
          return 'Imprimir lote'
        }

        return 'Fechar e imprimir lote'
      }

      return 'Imprimir etiqueta de lote'
    },
    printLabelsText() {
      return this.bathSelectedSamples?.length > 0
        ? `Imprimir etiquetas (${this.bathSelectedSamples.length})`
        : 'Imprimir etiquetas'
    },
    batchId() {
      return this.$route.params.batchid
    },
    isWSBatch() {
      return this.batch.source === 'AOLWS'
    },
    isWSBatchID() {
      return this.batchId?.startsWith('LT')
    },
    isViewing() {
      return !!this.batchId
    },
    label() {
      return this.batch?.barcode ?? 'a definir'
    },
    isBatchOpen() {
      return this.batch?.state === 'OPENNED'
    },
    isEditingDisabled() {
      return this.isAddingSample || this.isBatchFetching
    },
  },
  watch: {
    sampleBarcode(value) {
      const element = this.$refs.sampleBarcodeInputRef.$el

      if (element.value !== value) {
        element.value = value
      }
    },
    async batchId() {
      await this.fetchBatch()

      this.focusBarcodeInput()
    },
  },
  async mounted() {
    if (this.isViewing) {
      await this.fetchBatch()

      if (this.toggle.printerPlugin) {
        this.connectWebSocket()
      }
    }

    eventBus.$on(OPEN_PRINTER_PLUGIN_MODAL, () => {
      this.$refs.printerPluginDownloadModal.forceOpen()
    })

    this.$nextTick(() => {
      this.$refs.sampleBarcodeInputRef.$el.focus()
    })
  },
  beforeDestroy() {
    eventBus.$off(OPEN_PRINTER_PLUGIN_MODAL)
  },
  methods: {
    ...mapActions([
      'webSocketPrintAndTransmit',
      'webSocketPrintSamples',
      'printAndTransmit',
      'printSamples',
      'fetchBatchSamples',
      'initializeWebSocket',
      'updateWebSocketStatus',
    ]),
    async connectWebSocket() {
      try {
        await PrintWebSocketService.disconnect()
        await new Promise(resolve => setTimeout(resolve, 2000))

        this.updateWebSocketStatus('connecting')

        await this.initializeWebSocket()

        if (this.webSocketStatus.connectionState !== 'connected') {
          throw new Error('WebSocket connection failed')
        } else if (this.webSocketStatus.hasNewVersion) {
          this.$nextTick(() => {
            this.$refs.webSocketPluginDownloadModal.open()
          })
        }
      } catch (error) {
        console.error('Erro na conexão:', error)

        this.updateWebSocketStatus('disconnected')

        this.$nextTick(() => {
          this.$refs.webSocketPluginDownloadModal.open()
        })
      }
    },
    pushEventToAnalytics(action) {
      this.$analytics.pushAnalyticsEvent('ga-event', {
        category: 'Impressão de Etiquetas - Detalhes do Lote',
        action,
        'id-user': this.profile.id,
        'id-lab': this.profile.id_laboratory,
        'name-lab': this.profile.laboratoryName,
      })
    },
    validatePrintingSystemForBatch() {
      if (this.webSocketStatus.connectionState === 'connected') {
        this.webSocketPrintBatchTag()
      } else {
        this.$refs.printerPluginDownloadModal.forceOpen()
      }
    },
    validatePrintingSystemForAllSamples() {
      if (this.webSocketStatus.connectionState === 'connected') {
        this.webSocketPrintAllSamplesFromBatch()
      } else {
        this.$refs.printerPluginDownloadModal.forceOpen()
      }
    },
    printAllSamplesFromBatch() {
      const source = this.batch.source
      let samplesId =
        this.bathSelectedSamples?.length > 0
          ? this.bathSelectedSamples
          : (this.batchSamplesSorted.samples ?? [])

      if (source !== 'AOL2') {
        samplesId = samplesId.map(elem => elem.barcode)
        samplesId = { barcodeList: samplesId }
      } else {
        samplesId = samplesId.map(elem => ({
          sample_id: elem.sample_id,
          batch: {
            id: elem.batch_id || null,
          },
        }))
      }

      this.printSamples({
        samplesId,
        printBatch: false,
        source,
      })
        .then(() => {
          this.refreshBatchDetailsAfterSamplePrint()
        })
        .catch(() => {
          this.refreshBatchDetailsAfterSamplePrint()
        })

      this.pushEventToAnalytics(
        'Impressão de todas etiquetas de amostras ' + this.batch.source
      )
    },
    webSocketPrintAllSamplesFromBatch() {
      if (this.defaultPrinter) {
        let samplesId =
          this.bathSelectedSamples?.length > 0
            ? this.bathSelectedSamples
            : (this.batchSamplesSorted.samples ?? [])

        samplesId = samplesId.map(elem => elem.barcode || elem.bar_code)
        samplesId = {
          barcodeList: samplesId,
        }

        this.webSocketPrintSamples({
          samplesId,
          printBatch: false,
          batch: this.batch,
        })
          .then(() => {
            this.refreshBatchDetailsAfterSamplePrint()
          })
          .catch(() => {
            this.refreshBatchDetailsAfterSamplePrint()
          })

        this.pushEventToAnalytics(
          'Impressão de todas etiquetas de amostras ' + this.batch.source
        )
      } else {
        this.openConfigModal()
      }
    },
    webSocketPrintBatchTag() {
      if (this.defaultPrinter) {
        this.$refs.printingModal.open()

        this.webSocketPrintAndTransmit({
          samples: [],
          barcode: this.batch.barcode,
          printOnlyBatchTag: true,
          source: this.batch.source,
        })
          .then(() => {
            this.$refs.printingModal.close()
            this.fetchBatch()
          })
          .catch(() => {
            this.$refs.printingModal.close()
          })

        this.pushEventToAnalytics('Impressão de etiqueta de lote')
      } else {
        this.openConfigModal()
      }
    },
    textToBase64Barcode(barcode, format) {
      const JsBarcode = require('jsbarcode')
      const canvas = document.createElement('canvas')

      JsBarcode(canvas, barcode, {
        format,
        displayValue: false,
      })

      return canvas.toDataURL('image/png')
    },
    getPackagingStatusName(batch) {
      switch (batch) {
        case 25:
          return 'Congelado'
        case 26:
          return 'Refrigerado'
        case 27:
          return 'Ambiente'
        case 208:
          return 'Refrigerado Seco'
        default:
          return 'Outros'
      }
    },
    printBatchTag() {
      this.$refs.printingModal.open()

      this.printAndTransmit([
        {
          batchIdentifier: this.batch.barcode,
          sampleSortColumn: this.$route.query.orderPrintBy,
          printOnlyBatchTag: 1,
        },
      ])
        .then(response => {
          if (response === 428) {
            this.$refs.printServerAlertModal.openPrintServerAlertModal()
          }

          this.$refs.printingModal.close()

          this.fetchBatch()
        })
        .catch(() => {
          this.$refs.printingModal.close()
        })

      this.pushEventToAnalytics('Impressão de etiqueta de lote')
    },
    openConfigModal() {
      this.$refs.printerModal.open()
    },
    handleDownloadPluginRequested() {
      this.$refs.printerPluginDownloadModal.forceOpen()
    },
    handleRequestBatchFetching() {
      this.fetchBatch()
    },
    translateBatchSampleValdation({ data }) {
      return data.type === 'validation' ? data?.description : data?.message
    },
    translateBatchCreationError(error, defaultMessage) {
      const data = error && error.response ? error.response.data : null

      if (!data) return defaultMessage

      return data.description || data.message || data.error || defaultMessage
    },
    async handleInputBarcode(event) {
      this.sampleBarcode = event.target.value
      this.lastInputType = event.inputType

      this.$nextTick(async () => {
        const isPossiblyScanner = event.timeStamp - this.lastInputTimestamp < 20
        const isBarcodeValid = /^\d{12}$/.test(this.sampleBarcode.trim())

        if (isBarcodeValid && this.lastInputType === 'insertText' && isPossiblyScanner) {
          await this.handleAddSample()
        }

        this.lastInputTimestamp = event.timeStamp
      })
    },
    async handleAddSample() {
      this.isAddingSample = true

      try {
        const sampleValidationResponse = await sampleService.getSampleByBarcode(
          this.sampleBarcode
        )
        const sampleValidation = sampleValidationResponse?.data
        const isSampleInABatch = !!sampleValidation.batch_identifier
        const isSampleFromWS = sampleValidation.origem === 'AOLWS'

        if (sampleValidation) {
          if (this.isViewing) {
            if (this.batch.samplesCollected) {
              this.isAddingSample = false

              notify({
                title: 'Erro:',
                message:
                  'Não é possível adicionar amostras a um lote que já foi coletado.',
              })

              this.focusBarcodeInput()

              return
            }

            if (sampleValidation.isPaternity) {
              this.isAddingSample = false

              notify({
                title: 'Erro:',
                message:
                  'Não é possível adicionar amostras de teste de paternidade à um lote existente.',
              })

              this.focusBarcodeInput()

              return
            }

            const isFromDifferentSource = this.isWSBatch
              ? !isSampleFromWS
              : isSampleFromWS

            if (isFromDifferentSource) {
              this.isAddingSample = false

              notify({
                title: 'Erro:',
                message: `Não é possível adicionar amostras ${isSampleFromWS ? 'LIS' : 'AOL'} em um lote ${!isSampleFromWS ? 'LIS' : 'AOL'}.`,
              })

              this.focusBarcodeInput()

              return
            }

            const [batchDestinationAcronym] = this.batch.mnemonic.split('.')
            const [sampleDestinationAcronym] = sampleValidation.batch_label.split('.')
            const isDifferentDestination =
              batchDestinationAcronym !== sampleDestinationAcronym

            if (isDifferentDestination) {
              this.isAddingSample = false

              notify({
                title: 'Erro:',
                message:
                  'Não é possível adicionar amostras com destino diferente do lote atual.',
              })

              this.focusBarcodeInput()

              return
            }

            const isDifferentPackaging =
              this.batch.packagingId !== sampleValidation.packaging_type

            if (isDifferentPackaging) {
              this.isAddingSample = false

              notify({
                title: 'Erro:',
                message:
                  'Não é possível adicionar amostras com acondicionamento diferente do lote atual.',
              })

              this.focusBarcodeInput()

              return
            }

            if (isSampleInABatch) {
              const isInSameBatch = this.isWSBatch
                ? this.batch.batchIdentifier === sampleValidation.batch_identifier
                : this.batch.id === sampleValidation.batch_id

              if (isInSameBatch) {
                this.isAddingSample = false

                notify({
                  title: 'Erro:',
                  message: 'A amostra já pertence ao lote atual.',
                })

                this.focusBarcodeInput()

                return
              }

              this.sampleToMove = sampleValidation
              this.isAddingSample = false

              this.$refs.moveSampleToCurrentBatchModalRef.open()

              return
            }
          } else if (isSampleInABatch) {
            if (sampleValidation.isPaternity) {
              this.isAddingSample = false

              notify({
                title: 'Erro:',
                message:
                  'Não é possível criar um novo lote para amostras de teste de paternidade que já estão loteadas.',
              })

              this.focusBarcodeInput()

              return
            }

            this.sampleToMove = sampleValidation
            this.isAddingSample = false

            this.$refs.moveSampleToCurrentBatchModalRef.open()

            return
          }

          await this.addSample({
            isSampleFromWS,
          })

          this.sampleBarcode = ''
          this.isAddingSample = false

          this.focusBarcodeInput()
        } else {
          this.isAddingSample = false

          notify({
            title: 'Erro:',
            message: 'Não foi possível validar a amostra.',
          })

          this.focusBarcodeInput()
        }
      } catch (error) {
        console.error('Error validating sample by barcode.', error)

        this.isAddingSample = false

        notify({
          title: 'Erro:',
          message: 'Não foi possível validar a amostra.',
        })

        this.focusBarcodeInput()
      }
    },
    async fetchBatch() {
      this.isBatchFetching = true

      if (this.batchId) {
        try {
          const response = await batchService.getBatchDetails({
            id: this.batchId,
            isWs: this.isWSBatchID,
          })

          this.batch = response.data
        } catch (error) {
          console.error('Error fetching batch details.', error)

          notify({
            title: 'Erro:',
            message: 'Erro ao carregar detalhes do lote.',
          })

          this.batch = PLACEHOLDER_BATCH
        }
      } else {
        this.batch = PLACEHOLDER_BATCH
      }

      this.isBatchFetching = false
    },
    async addSample({ isSampleFromWS }) {
      const payload = {
        samples: [
          {
            barcode: this.sampleBarcode,
          },
        ],
      }

      if (this.isViewing) {
        payload.batchIdentifier = this.batch.batchIdentifier

        if (this.isWSBatch) {
          await this.insertSampleIntoWSBatch(payload)
        } else {
          await this.insertSampleIntoBatch(payload)
        }

        await Promise.all([this.updateSamplesList(), this.fetchBatch()])
      } else {
        let createdBatch

        if (isSampleFromWS) {
          createdBatch = await this.createWSBatch(payload)
        } else {
          createdBatch = await this.createBatch(payload)
        }

        const createdBatchID = createdBatch?.id ?? createdBatch?.batchIdentifier

        if (createdBatchID) {
          this.$router.replace(`/print-tag/batches/${createdBatchID}`)
        }
      }
    },
    async insertSampleIntoBatch(payload) {
      try {
        await batchService.insertSampleIntoBatch(payload)
      } catch (error) {
        console.error('Error inserting sample into batch.', error)

        const message =
          error.response?.data?.description ?? 'Erro ao tentar inserir amostra no lote.'

        notify({
          title: 'Erro:',
          message,
        })
      }
    },
    async insertSampleIntoWSBatch(payload) {
      try {
        await batchService.insertSampleIntoWSBatch(payload)
      } catch (error) {
        console.error('Error inserting sample into batch.', error)

        const message =
          error.response?.data?.description ?? 'Erro ao tentar inserir amostra no lote.'

        notify({
          title: 'Erro:',
          message,
        })
      }
    },
    async createBatch(payload) {
      try {
        const response = await batchService.createBatch(payload)

        return response?.data
      } catch (error) {
        console.error('Error creating batch.', error)

        notify({
          title: 'Erro:',
          message: this.translateBatchCreationError(
            error,
            'Erro ao tentar criar novo lote.'
          ),
        })
      }
    },
    async createWSBatch(payload) {
      try {
        const response = await batchService.createWSBatch(payload)

        return response?.data
      } catch (error) {
        console.error('Error creating WS batch.', error)

        notify({
          title: 'Erro:',
          message: this.translateBatchCreationError(
            error,
            'Erro ao tentar criar novo lote.'
          ),
        })
      }
    },
    async handleMoveSampleToCurrentBatch() {
      this.isSampleBeingMoved = true

      try {
        this.$refs.moveSampleToCurrentBatchModalRef.close()
        this.$refs.moveSampleToBatchLoadingModal.open()

        let removeSampleResponse

        if (this.sampleToMove.origem === 'AOLWS') {
          if (this.isViewing) {
            removeSampleResponse = await batchService.removeSamplesFromWSBatchToMove(
              this.sampleBarcode,
              this.sampleToMove.batch_id ?? this.sampleToMove.batch_identifier,
              this.batch.batchIdentifier
            )
          } else {
            removeSampleResponse = await batchService.removeSamplesFromWSBatch(
              this.sampleBarcode,
              this.sampleToMove.batch_id ?? this.sampleToMove.batch_identifier
            )
          }
        } else {
          removeSampleResponse = await batchService.removeSamplesFromBatch(
            this.sampleBarcode,
            this.sampleToMove.batch_id,
            this.isSampleBeingMoved
          )
        }

        const successfullyRemoved = /^2\d{2}$/.test(removeSampleResponse.status)

        if (successfullyRemoved) {
          if (this.isViewing) {
            if (this.isWSBatch) {
              await this.insertSampleIntoWSBatch({
                batchIdentifier: this.batch.batchIdentifier,
                samples: [
                  {
                    barcode: this.sampleBarcode,
                  },
                ],
              })
            } else {
              await this.insertSampleIntoBatch({
                batchIdentifier: this.batch.batchIdentifier,
                samples: [
                  {
                    barcode: this.sampleBarcode,
                  },
                ],
              })
            }

            await Promise.all([this.updateSamplesList(), this.fetchBatch()])
          } else {
            const isSampleFromWS = this.sampleToMove.origem === 'AOLWS'
            const batchPayload = {
              samples: [
                {
                  barcode: this.sampleBarcode,
                },
              ],
            }
            const createdBatch = isSampleFromWS
              ? await this.createWSBatch(batchPayload)
              : await this.createBatch(batchPayload)
            const createdBatchID = createdBatch?.id ?? createdBatch?.batchIdentifier

            this.$router.replace(`/print-tag/batches/${createdBatchID}`)
          }
        }
      } catch (error) {
        console.error('Error moving sample to current batch.', error)

        if (error.response?.status === 422) {
          const message = this.translateBatchSampleValdation(error.response)

          notify({ message })
        } else {
          notify({
            title: 'Erro:',
            message: 'Erro ao tentar mover amostra para lote atual.',
          })
        }
      }

      this.$refs.moveSampleToCurrentBatchModalRef.close()
      this.$refs.moveSampleToBatchLoadingModal.close()

      this.sampleToMove = undefined
      this.isSampleBeingMoved = false

      this.focusBarcodeInput()
    },
    async handleCancelMoveSampleToCurrentBatch() {
      this.$refs.moveSampleToCurrentBatchModalRef.close()
      this.$refs.moveSampleToBatchLoadingModal.close()

      this.sampleToMove = undefined
      this.sampleBarcode = ''

      this.focusBarcodeInput()
    },
    async updateSamplesList() {
      await this.fetchBatchSamples({
        batchId: this.batchId,
      })
    },
    async refreshBatchDetailsAfterSamplePrint() {
      await refreshBatchDetailsAfterSamplePrint(this)
    },
    handleBack() {
      this.$router.replace('/print-tag/batches')
    },
    focusBarcodeInput() {
      this.$nextTick(() => {
        this.$refs.sampleBarcodeInputRef.$el.focus()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

.create-batch {
  &-view {
    height: $contentContainerHeight;
    padding-top: $mainHeaderHeight;

    .batch-card {
      height: fit-content;
    }
  }

  &-print-buttons {
    display: flex;
    align-items: center;
    gap: 1rem;

    .btn {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      font-family: 'Exo 2';
      font-size: 0.75rem;
      font-weight: 600;
      height: 40px;
      line-height: 0;
      text-transform: uppercase;

      .md-icon {
        min-width: auto;
        min-height: auto;
        width: 1rem;
        height: 1rem;
      }
    }
  }

  &-header {
    display: flex;
    align-items: center;
    gap: 1rem;

    &-left {
      display: flex;
      align-items: center;
      gap: 1rem;
      flex: 1;
    }
  }

  &-card {
    margin-bottom: 1.5rem;
  }

  &-form {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    width: 50%;
    max-width: 100%;

    .btn {
      height: 40px;
      font-family: 'Exo 2';
      font-size: 0.875rem;
      font-weight: 600;
    }
  }

  &-input-sample-code {
    flex: 1;
    padding: 0 0.5rem;
    margin: 0;
    height: 40px;
    min-height: auto;
    border: solid 1px #cccccc;
    border-radius: 0.25rem;

    &::after {
      display: none;
    }

    .md-input {
      height: 100%;
      font-family: 'Exo 2';
      font-size: 0.875rem;

      &-placeholder {
        font-family: 'Exo 2';
        font-size: 0.875rem;
        color: #999999;
      }
    }

    .md-icon {
      height: 100%;
      margin: 0;
      color: $theme-primary-color !important;
    }
  }

  &-details {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;

    :deep(.header-card) {
      margin-bottom: 0 !important;
    }

    &-preview {
      width: $batch-card-min-w;
    }

    &-samples {
      flex: 1;
    }
  }
}

.page-back-button {
  color: $theme-primary-color;

  /** @note Necessary rule to reset some unnecessary inherited styles (Due to the usage of style tag without scoped). */
  background-color: transparent;
  height: auto;
  font-size: medium;
  font-weight: normal;
  border: initial;
  transition: none;
  animation-duration: initial;
  border-radius: 0;
}

.move-sample-current-batch-modal {
  max-width: 480px;

  &-header {
    margin-bottom: 1rem;
  }

  &-title {
    font-size: 1.25rem;
    font-weight: 600;
  }

  &-body {
    margin-bottom: 1rem;
  }

  &-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }

  &-main-button {
    display: flex;
    align-items: center;
    color: #ffffff;
    gap: 0.5rem;
  }
}

::v-deep(.move-sample-current-batch-modal-link) {
  color: $theme-primary-color !important;
  &:hover {
    text-decoration: underline !important;
  }
}

::v-deep(.card-form__container .title-main) {
  color: $theme-primary-color;
}
</style>
